import type { IRegistrationField } from '@src/api/types/auth.types';

import {
  IAchieve,
  IAchieveYourTargetWeight,
  IBadHabits,
  ICurrentBodyType,
  IDailyMeals,
  IDietaryPattern,
  IFastingWeekends,
  IFeelHungry,
  IGender,
  IIdealBodyType,
  IIntermittentFasting,
  ILastHappy,
  ILifestyle,
  ILoseWeightFor,
  IPhysicallyActive,
  IPrepareMeals,
  ISleep,
  IStairsBreath,
  ITimeBreakfast,
  ITimeDinner,
  ITimeLunch,
  IWalkingTypicalDay,
  IWorkingHours,
} from '../constants';
import {
  ICurrentWeightForm,
  IHeightForm,
  ITargetWeightForm,
  IYourAgeForm,
} from './forms.interfaces';
import { IPersonAreasState } from './socials.interfaces';

export const QUIZ_STEPS = {
  INTERMITTENT_FASTING: 'questionnaire.familiar_level',
  ACHIEVE: 'achieve',
  GENDER: 'profile.gender',
  CURRENT_BODY_TYPE: 'current_body_type',
  IDEAL_BODY_TYPE: 'ideal_body_type',
  LAST_HAPPY: 'last_happy',
  FEEL_HUNGRY: 'questionnaire.most_hungry_time',
  TIME_BREAKFAST: 'questionnaire.breakfast_time',
  TIME_LUNCH: 'questionnaire.lunch_time',
  TIME_DINNER: 'questionnaire.dinner_time',
  PREPARE_MEALS: 'prepare_meals',
  FASTING_WEEKENDS: 'questionnaire.fasting_on_weekends_type',
  DIETARY_PATTERN: 'dietary_pattern',
  SLEEP: 'questionnaire.sleep_time',
  PHYSICALLY_ACTIVE: 'profile.activity_level',
  WORKING_HOURS: 'questionnaire.working_hours',
  STAIRS_BREATH: 'stairs_breath',
  WALKING_TYPICAL_DAY: 'walking_typical_day',
  DAILY_MEALS: 'daily_meals',
  DRINK_WATER: 'drink_water',
  BAD_HABITS: 'bad_habits',
  LIFESTYLE: 'lifestyle',
  ACHIEVE_YOUR_TARGET_WEIGHT: 'achieve_your_target_weight',
  HEIGHT: 'profile.height',
  CURRENT_WEIGHT: 'profile.weight.start',
  TARGET_WEIGHT: 'profile.weight.target',
  YOUR_AGE: 'profile.age',
  LOSE_WEIGHT_FOR: 'lose_weight_for',
  USERS: 'users',
  VITAL_BENEFITS: 'vital_benefits',
  SUSTAINABLE_WEIGHT_LOSS: 'sustainable_weight_loss',
  AREAS: 'areas',
  WELLNESS: 'wellness',
  EVENT_DATE: 'event_date',
  GRAPH: 'graph',
  PLAN: 'plan',
  EMAIL: 'email',
  YOUR_WEIGHT_GRAPH: 'your_weight_graph',
  LOSE_TWICE: 'lose_twice',
  RESTRICTIVE_DIETS: 'restrictive_diets',
  RECEIVE_EMAILS: 'receive_emails',
  NAME: 'name',
  SPECIAL_DISCOUNT: 'special_discount',
} as const;

export type IQuizSteps = (typeof QUIZ_STEPS)[keyof typeof QUIZ_STEPS];

export interface IStateContextData {
  stepIndex: number;

  personAreas: IPersonAreasState;
  age: null | number;
  intermittentFasting: IIntermittentFasting | null;
  achieve: IAchieve[] | null;
  gender: IGender | null;
  current_body_type: ICurrentBodyType | null;
  ideal_body_type: IIdealBodyType | null;
  last_happy: ILastHappy | null;
  feel_hungry: IFeelHungry | null;
  time_breakfast: ITimeBreakfast | null;
  time_lunch: ITimeLunch | null;
  time_dinner: ITimeDinner | null;
  prepare_meals: IPrepareMeals | null;
  fasting_weekends: IFastingWeekends | null;
  dietary_pattern: IDietaryPattern | null;
  sleep: ISleep | null;
  physically_active?: IPhysicallyActive | null;
  working_hours: IWorkingHours | null;
  stairs_breath: IStairsBreath | null;
  walking_typical_day: IWalkingTypicalDay | null;
  daily_meals: IDailyMeals | null;
  drink_water: number | null;
  bad_habits: IBadHabits[] | null;
  lifestyle: ILifestyle | null;
  achieve_your_target_weight: IAchieveYourTargetWeight | null;
  height: IHeightForm | null;
  currentWeight: ICurrentWeightForm | null;
  targetWeight: ITargetWeightForm | null;
  yourAge: IYourAgeForm['yourAge'];
  lose_weight_for: ILoseWeightFor | null;
  event_date?: Date;
  email?: string;
  name?: string;
}

export interface IQuizStepsHistoryItem {
  step: IQuizSteps;
  registrationFields?: readonly IRegistrationField[];
  isSocial?: boolean;
}

export type IQuizStepsHistory = IQuizStepsHistoryItem[];
